import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerPairingService, type PairedRunner } from '../../services/runner-pairing.service';
import { RunnerListComponent } from '../runner-list/runner-list.component';
import { saveBlob } from '../../shared/file-download';

/** Sechs Ziffern, wie auch immer der Mensch sie gruppiert eintippt. */
const SIX_DIGITS = /^\d{6}$/u;

/**
 * „Rechner verbinden" — das Gegenstück zu dem Code, den der Runner anzeigt.
 *
 * Die Reihenfolge ist Absicht: Nicht der Runner weist sich aus, sondern der hier angemeldete
 * Mensch bestätigt, dass dieser Rechner zu ihm gehört. Das Zugangstoken, das der Runner danach
 * bekommt, ist ein eigener Zugang des Rechners und steht gleich darunter unter „Meine Rechner"
 * (`app-runner-list`) — dort laesst er sich auch wieder trennen. Fremde Rechner zeigt /runners.
 *
 * **Ohne Design-Entwurf.** Das Bundle v2026-08-25-d1654a822bf deckt unter `page-tokens.jsx` nur
 * die Provider-Tokens ab, diesen Bereich kennt es nicht. Umgesetzt im Stil der umgebenden
 * Kontoseite — gleiche Abstände, gleiche Klassen, keine neuen Farben oder Formen. Das ist eine
 * Lücke, keine Entscheidung: Sobald ein Entwurf vorliegt, gehört das hier angeglichen. Das gilt
 * auch für die Etiketten je Rechner (NEX-1257): Marken im Stil der Voreinstellungs-Marke, das
 * Eingabefeld im Stil der Zeile. Der Hinweis ohne Paket (NEX-1511) folgt dem gestrichelten Kasten
 * „Kein passender Runner dabei?" aus dem Ausführen-Dialog (`page-testfaelle.jsx`).
 */
@Component({
  selector: 'app-runner-pairing-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterLink, RunnerListComponent, TranslatePipe, MatIcon, MatProgressSpinner],
  templateUrl: './runner-pairing-section.component.html',
  styleUrl: './runner-pairing-section.component.scss',
})
export class RunnerPairingSectionComponent implements OnInit {
  private readonly service = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly code = signal('');
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly connectedRunner = signal<string | null>(null);
  readonly packages = signal<string[]>([]);
  /** Solange die Liste lädt, steht weder Knopf noch Hinweis da — sonst flackert „kein Paket“ auf. */
  readonly packagesState = signal<'loading' | 'loaded' | 'failed'>('loading');
  /** Nur die eigenen Rechner; alle anderen stehen unter /runners (NEX-1729). */
  readonly myRunners = signal<PairedRunner[]>([]);
  readonly downloading = signal<string | null>(null);

  readonly canSubmit = computed(() => SIX_DIGITS.test(this.digits()) && !this.busy());

  ngOnInit(): void {
    this.reloadRunners();
    this.service
      .listPackages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (platforms) => {
          this.packages.set(platforms);
          this.packagesState.set('loaded');
        },
        // Ohne Hinweis stünde Schritt 1 ohne Download da (NEX-1508). Das Eingabefeld bleibt
        // trotzdem nutzbar: Wer das Paket schon hat, kann weiter verbinden.
        error: () => this.packagesState.set('failed'),
      });
  }

  /** Die Liste ist der Gegenbeweis zum Hinweis "widerrufen Sie das Token" — ohne sie ginge das nicht. */
  private reloadRunners(): void {
    this.service
      .listRunners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runners) => this.myRunners.set(runners.filter((runner) => runner.mine)),
        error: () => this.myRunners.set([]),
      });
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.busy.set(true);
    this.error.set(null);
    this.connectedRunner.set(null);

    this.service
      .confirm(this.digits())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (confirmation) => {
          this.connectedRunner.set(confirmation.runnerName);
          this.code.set('');
          this.busy.set(false);
          // Der eben verbundene Rechner gehoert sofort in die Liste, sonst sieht der Nutzer den
          // Erfolg gemeldet, aber nicht belegt.
          this.reloadRunners();
        },
        error: (failure: HttpErrorResponse) => {
          // Der Code bleibt stehen — ein Tippfehler lässt sich so korrigieren, statt ihn vom
          // Runner-Fenster erneut abzulesen.
          this.error.set(messageFor(failure.status));
          this.busy.set(false);
        },
      });
  }

  download(platform: string): void {
    this.downloading.set(platform);
    this.service
      .downloadPackage(platform)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          saveBlob(blob, `testoffice-runner-${platform}.zip`);
          this.downloading.set(null);
        },
        error: () => {
          this.error.set('runnerPairing.error.downloadFailed');
          this.downloading.set(null);
        },
      });
  }

  private digits(): string {
    return this.code().replace(/\D/gu, '');
  }
}

function messageFor(status: number): string {
  if (status === 404) {
    return 'runnerPairing.error.unknownCode';
  }
  if (status === 429) {
    return 'runnerPairing.error.tooManyAttempts';
  }
  return 'runnerPairing.error.generic';
}
